import { DestroyRef, Component, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import Keycloak from 'keycloak-js';
import { combineLatest, Observable } from 'rxjs';

import { CompanyContextService, CompanyOption } from '../../core/services/company-context.service';
import { AccountSetupService } from '../../core/services/account-setup.service';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-company-context-panel',
  templateUrl: './company-context-panel.component.html',
  styleUrl: './company-context-panel.component.css',
  standalone: false
})
export class CompanyContextPanelComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  readonly companies$: Observable<CompanyOption[]>;
  readonly selectedCompanyId$: Observable<string | null>;
  readonly loading$: Observable<boolean>;
  readonly error$: Observable<string | null>;

  showNoCompaniesDialog = false;

  private isAuthenticated = false;
  private initialLoadFinished = false;
  private currentCompanies: CompanyOption[] = [];
  private currentLoading = false;
  private currentError: string | null = null;
  private setupRequired = false;

  private readonly keycloak = inject(Keycloak);

  constructor(
    private readonly companyContext: CompanyContextService,
    private readonly accountSetupService: AccountSetupService
  ) {
    this.companies$ = this.companyContext.companies$;
    this.selectedCompanyId$ = this.companyContext.selectedCompanyId$;
    this.loading$ = this.companyContext.loading$;
    this.error$ = this.companyContext.error$;

    combineLatest([this.companies$, this.loading$, this.error$])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([companies, loading, error]) => {
        this.currentCompanies = companies;
        this.currentLoading = loading;
        this.currentError = error;
        this.updateNoCompaniesDialogVisibility();
      });

    this.accountSetupService.setupRequired$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((state) => {
        if (state.loading) {
          return;
        }
        this.setupRequired = state.required;
        this.updateNoCompaniesDialogVisibility();
      });
  }

  async ngOnInit(): Promise<void> {
    await this.waitForKeycloakInitialization();
    this.isAuthenticated = this.keycloak.authenticated ?? false;

    if (this.isAuthenticated) {
      try {
        await this.companyContext.loadCompanies();
      } catch {
        // Error state is exposed by the service and rendered in the panel.
      } finally {
        this.initialLoadFinished = true;
        this.updateNoCompaniesDialogVisibility();
      }
    }
  }

  onCompanyChange(companyId: string | null): void {
    if (companyId) {
      this.companyContext.selectCompany(companyId);
    }
  }

  goToCompanies(): void {
    window.location.href = `${environment.adminPanelUrl}/companies`;
  }

  private updateNoCompaniesDialogVisibility(): void {
    this.showNoCompaniesDialog =
      this.isAuthenticated &&
      this.initialLoadFinished &&
      !this.setupRequired &&
      !this.currentLoading &&
      !this.currentError &&
      this.currentCompanies.length === 0;
  }

  private async waitForKeycloakInitialization(): Promise<void> {
    const timeoutMs = 10_000;
    const pollMs = 50;
    const startedAt = Date.now();

    while (!this.keycloak.didInitialize && Date.now() - startedAt < timeoutMs) {
      await new Promise((resolve) => setTimeout(resolve, pollMs));
    }
  }
}
